// novelaiMeta.js
// PNG tEXt/zTXt/iTXt, WebP XMP/EXIF, alpha LSB "stealth_pngcomp" 추출 + NovelAI 파서

const textDecoderLatin1 = new TextDecoder('latin1');
const textDecoderUTF8 = new TextDecoder('utf-8');

const toU32BE = (dv, off) => dv.getUint32(off, false);
const toU32LE = (dv, off) => dv.getUint32(off, true);

function readNullTerminated(data, start, decoder) {
  let i = start;
  while (i < data.length && data[i] !== 0) i++;
  const value = decoder.decode(data.subarray(start, i));
  return { value, next: i + 1 };
}

async function decompressStream(data, format /* 'gzip' | 'deflate' */) {
  if (typeof DecompressionStream !== 'undefined') {
    const ds = new DecompressionStream(format);
    const stream = new Blob([data]).stream().pipeThrough(ds);
    const ab = await new Response(stream).arrayBuffer();
    return new Uint8Array(ab);
  }
  throw new Error('DecompressionStream not available');
}

async function inflateZlib(data) {
  return decompressStream(data, 'deflate');
}

async function gunzip(data) {
  return decompressStream(data, 'gzip');
}

/* ---------- PNG: tEXt / zTXt / iTXt ---------- */
export async function parsePNGTextChunks(arrayBuffer) {
  const u8 = new Uint8Array(arrayBuffer);
  const dv = new DataView(arrayBuffer);

  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  for (let i = 0; i < 8; i++) {
    if (u8[i] !== signature[i]) throw new Error('Not a PNG file');
  }

  let pos = 8;
  const texts = {};
  const rawTextChunks = [];

  while (pos + 8 <= u8.length) {
    const length = toU32BE(dv, pos); pos += 4;
    const typeBytes = u8.subarray(pos, pos + 4); pos += 4;
    const type = String.fromCharCode(...typeBytes);
    const data = u8.subarray(pos, pos + length); pos += length;
    pos += 4; // skip CRC

    if (type === 'tEXt') {
      const { value: keyword, next } = readNullTerminated(data, 0, textDecoderLatin1);
      const text = textDecoderLatin1.decode(data.subarray(next));
      texts[keyword] = text;
      rawTextChunks.push({ type, keyword, text });
    } else if (type === 'zTXt') {
      const { value: keyword, next } = readNullTerminated(data, 0, textDecoderLatin1);
      const compMethod = data[next];
      const compData = data.subarray(next + 1);
      if (compMethod !== 0) throw new Error('Unsupported zTXt compression method');
      const inflated = await inflateZlib(compData);
      const text = textDecoderLatin1.decode(inflated);
      texts[keyword] = text;
      rawTextChunks.push({ type, keyword, text });
    } else if (type === 'iTXt') {
      let idx = 0;
      const { value: keyword, next: n1 } = readNullTerminated(data, idx, textDecoderLatin1); idx = n1;
      const compFlag = data[idx++]; // 0 or 1
      const compMethod = data[idx++]; // 0
      const { value: languageTag, next: n2 } = readNullTerminated(data, idx, textDecoderUTF8); idx = n2;
      const { value: translatedKeyword, next: n3 } = readNullTerminated(data, idx, textDecoderUTF8); idx = n3;
      const textBytes = data.subarray(idx);
      let text;
      if (compFlag === 1) {
        if (compMethod !== 0) throw new Error('Unsupported iTXt compression method');
        const inflated = await inflateZlib(textBytes);
        text = textDecoderUTF8.decode(inflated);
      } else {
        text = textDecoderUTF8.decode(textBytes);
      }
      texts[keyword] = text;
      rawTextChunks.push({ type, keyword, languageTag, translatedKeyword, text });
    }

    if (type === 'IEND') break;
  }

  return { texts, rawTextChunks };
}

/* ---------- WebP: RIFF XMP / EXIF (+optional custom tEXt/iTXt/zTXt) ---------- */
function parseFirstJsonCandidate(str) {
  const idxs = [];
  for (let i = 0; i < str.length; i++) if (str[i] === '{') idxs.push(i);
  for (const start of idxs) {
    const cap = Math.min(str.length, start + 200000);
    for (let end = cap; end > start; end--) {
      const s = str.slice(start, end);
      if (s.includes('"sampler"') || s.includes('"uc"') || s.includes('"Description"')) {
        try { return JSON.parse(s); } catch (e) {}
      }
    }
  }
  return null;
}

export async function parseWebPMetadata(arrayBuffer) {
  const u8 = new Uint8Array(arrayBuffer);
  const dv = new DataView(arrayBuffer);

  if (u8.length < 12) throw new Error('Not a WebP file');
  const riff = String.fromCharCode(u8[0], u8[1], u8[2], u8[3]);
  const webp = String.fromCharCode(u8[8], u8[9], u8[10], u8[11]);
  if (riff !== 'RIFF' || webp !== 'WEBP') throw new Error('Not a WebP file');

  let pos = 12;
  const chunks = [];
  let xmpXml = null;
  let exifBytes = null;
  const customTexts = {};

  while (pos + 8 <= u8.length) {
    const fourCC = String.fromCharCode(u8[pos], u8[pos + 1], u8[pos + 2], u8[pos + 3]);
    const size = toU32LE(dv, pos + 4);
    const dataStart = pos + 8;
    const dataEnd = Math.min(dataStart + size, u8.length);
    const payload = u8.subarray(dataStart, dataEnd);
    chunks.push({ fourCC, size });

    if (fourCC === 'XMP ') {
      xmpXml = textDecoderUTF8.decode(payload);
    } else if (fourCC === 'EXIF') {
      exifBytes = payload.slice();
    } else if (fourCC === 'tEXt' || fourCC === 'iTXt' || fourCC === 'zTXt') {
      try {
        if (fourCC === 'tEXt') {
          const { value: keyword, next } = readNullTerminated(payload, 0, textDecoderLatin1);
          const text = textDecoderLatin1.decode(payload.subarray(next));
          customTexts[keyword] = text;
        } else if (fourCC === 'zTXt') {
          const { value: keyword, next } = readNullTerminated(payload, 0, textDecoderLatin1);
          const compMethod = payload[next];
          const compData = payload.subarray(next + 1);
          if (compMethod === 0) {
            const inflated = await inflateZlib(compData);
            const text = textDecoderLatin1.decode(inflated);
            customTexts[keyword] = text;
          }
        } else if (fourCC === 'iTXt') {
          let idx = 0;
          const { value: keyword, next: n1 } = readNullTerminated(payload, idx, textDecoderLatin1); idx = n1;
          const compFlag = payload[idx++]; const compMethod = payload[idx++];
          const { value: languageTag, next: n2 } = readNullTerminated(payload, idx, textDecoderUTF8); idx = n2;
          const { value: translatedKeyword, next: n3 } = readNullTerminated(payload, idx, textDecoderUTF8); idx = n3;
          const textBytes = payload.subarray(idx);
          let text;
          if (compFlag === 1 && compMethod === 0) {
            const inflated = await inflateZlib(textBytes);
            text = textDecoderUTF8.decode(inflated);
          } else {
            text = textDecoderUTF8.decode(textBytes);
          }
          customTexts[keyword] = text;
        }
      } catch (e) {
        // ignore
      }
    }

    pos = dataStart + size + (size & 1);
  }

  let naiFromXmp = null;
  if (xmpXml) naiFromXmp = parseFirstJsonCandidate(xmpXml);

  let naiFromExif = null;
  if (exifBytes) {
    const exifStr = new TextDecoder('utf-8', { fatal: false }).decode(exifBytes);
    naiFromExif = parseFirstJsonCandidate(exifStr);
  }

  return { chunks, xmpXml, exifBytes, customTexts, naiFromXmp, naiFromExif };
}

/* ---------- Alpha LSB "stealth_pngcomp" ---------- */
function getImageDataFromBitmap(bitmap) {
  const w = bitmap.width, h = bitmap.height;
  if (typeof OffscreenCanvas !== 'undefined') {
    const oc = new OffscreenCanvas(w, h);
    const ctx = oc.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0);
    return ctx.getImageData(0, 0, w, h);
  }
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0);
    return ctx.getImageData(0, 0, w, h);
  }
  throw new Error('No canvas available to read pixels');
}

export async function extractStealthPNGCOMPFromImageSource(imageSource) {
  let imageData = null;

  if (typeof ImageData !== 'undefined' && imageSource instanceof ImageData) {
    imageData = imageSource;
  } else {
    const bitmap = await createImageBitmap(imageSource, { premultiplyAlpha: 'none' });
    imageData = getImageDataFromBitmap(bitmap);
  }

  const { data, width: w, height: h } = imageData;
  const HEADER = 'stealth_pngcomp';

  let row = 0, col = 0, bits = 0, byte = 0;

  function extractNextBit() {
    if (row < h && col < w) {
      const idx = (row * w + col) * 4 + 3;
      const bit = data[idx] & 1;
      bits += 1;
      byte = (byte << 1) | bit;
      row += 1;
      if (row === h) { row = 0; col += 1; }
      return true;
    }
    return false;
  }

  function getOneByte() {
    bits = 0; byte = 0;
    for (let i = 0; i < 8; i++) {
      if (!extractNextBit()) return null;
    }
    return byte;
  }

  function getNextNBytes(n) {
    const out = [];
    for (let i = 0; i < n; i++) {
      const b = getOneByte();
      if (b == null) break;
      out.push(b);
    }
    return new Uint8Array(out);
  }

  function read32BE() {
    const bytes = getNextNBytes(4);
    if (bytes.length !== 4) return null;
    return (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | (bytes[3]);
  }

  const magicBytes = getNextNBytes(HEADER.length);
  if (magicBytes.length !== HEADER.length) return null;
  const magic = textDecoderUTF8.decode(magicBytes);
  if (magic !== HEADER) return null;

  const bitLen = read32BE();
  if (bitLen == null || bitLen % 8 !== 0) return null;
  const byteLen = bitLen >>> 3;
  const payload = getNextNBytes(byteLen);
  if (payload.length !== byteLen) return null;

  const ungz = await gunzip(payload);
  const text = textDecoderUTF8.decode(ungz);

  let json = null;
  try { json = JSON.parse(text); } catch (e) {}

  return { raw: text, json };
}

/* ---------- NovelAI-style parsers ---------- */
export function parseNovelAIFromTextChunks(texts) {
  if (!texts || !texts.Comment) return null;
  try {
    const metadata = JSON.parse(texts.Comment);
    const params = texts.Description ?? '';
    const source = texts.Source ?? '';

    const samplerName = metadata.sampler;
    const samplerParams = {};
    for (const k of ['seed', 'strength', 'noise', 'scale']) {
      if (metadata[k] !== undefined) samplerParams[k === 'scale' ? 'cfg_scale' : k] = metadata[k];
    }

    const parsed = {
      generator: 'NOVELAI',
      sampler: {
        name: samplerName ?? null,
        parameters: samplerParams,
        prompts: [params.trim()].filter(Boolean),
        negative_prompts: metadata.uc ? [metadata.uc] : [],
      },
      model: null,
      raw_metadata: metadata,
      raw_parameters: { Comment: texts.Comment, Description: params, Source: source },
    };

    const m = source.match(/^(.*?)\s+([A-Z0-9]+)$/);
    if (m) parsed.model = { name: m[1], hash: m[2] };
    return parsed;
  } catch {
    return null;
  }
}

export function parseNovelAIFromStealth(stealth) {
  if (!stealth) return null;
  if (stealth.json && typeof stealth.json === 'object') {
    const metadata = stealth.json;
    const samplerParams = {};
    for (const k of ['seed', 'strength', 'noise', 'scale']) {
      if (metadata[k] !== undefined) samplerParams[k === 'scale' ? 'cfg_scale' : k] = metadata[k];
    }
    return {
      generator: 'NOVELAI',
      sampler: {
        name: metadata.sampler ?? null,
        parameters: samplerParams,
        prompts: metadata.prompt ? [metadata.prompt] : [],
        negative_prompts: metadata.uc ? [metadata.uc] : [],
      },
      model: metadata.model ? { name: metadata.model, hash: metadata.model_hash ?? null } : null,
      raw_metadata: metadata,
      raw_parameters: { Stealth: stealth.raw },
    };
  }
  return {
    generator: 'AUTOMATIC1111',
    sampler: { name: null, parameters: {}, prompts: [], negative_prompts: [] },
    model: null,
    raw_metadata: null,
    raw_parameters: { parameters: stealth.raw },
  };
}

export function parseNovelAIFromWebP(webpMeta) {
  if (!webpMeta) return null;

  const texts = {};
  if (webpMeta.customTexts) {
    for (const k of Object.keys(webpMeta.customTexts)) {
      if (k === 'Comment' || k === 'Description' || k === 'Source') {
        texts[k] = webpMeta.customTexts[k];
      }
    }
  }

  const candidate = webpMeta.naiFromXmp || webpMeta.naiFromExif || null;
  if (!texts.Comment && candidate) {
    texts.Comment = JSON.stringify(candidate);
    if (candidate.Description && !texts.Description) texts.Description = candidate.Description;
    if (candidate.Source && !texts.Source) texts.Source = candidate.Source;
  }

  const parsedFromTexts = parseNovelAIFromTextChunks(texts);
  if (parsedFromTexts) return parsedFromTexts;

  if (candidate && typeof candidate === 'object') {
    const samplerParams = {};
    for (const k of ['seed', 'strength', 'noise', 'scale']) {
      if (candidate[k] !== undefined) samplerParams[k === 'scale' ? 'cfg_scale' : k] = candidate[k];
    }
    return {
      generator: 'NOVELAI',
      sampler: {
        name: candidate.sampler ?? null,
        parameters: samplerParams,
        prompts: candidate.prompt ? [candidate.prompt] : [],
        negative_prompts: candidate.uc ? [candidate.uc] : [],
      },
      model: candidate.model ? { name: candidate.model, hash: candidate.model_hash ?? null } : null,
      raw_metadata: candidate,
      raw_parameters: { XMP_or_EXIF: candidate },
    };
  }

  return null;
}

/* ---------- Unified orchestrator ---------- */
export async function parseNovelAIMetadata({ arrayBuffer, mime, imageSource } = {}) {
  const out = {
    pngTexts: null,
    webpMeta: null,
    stealth: null,
    fromContainer: null,
    fromStealth: null,
    result: null,
  };

  if (arrayBuffer) {
    const u8 = new Uint8Array(arrayBuffer);
    const isPNG = u8.length >= 8 && u8[0] === 137 && u8[1] === 80 && u8[2] === 78 && u8[3] === 71;
    const isWebP = u8.length >= 12 && String.fromCharCode(u8[0], u8[1], u8[2], u8[3]) === 'RIFF' &&
                   String.fromCharCode(u8[8], u8[9], u8[10], u8[11]) === 'WEBP';

    if (mime === 'image/png' || isPNG) {
      out.pngTexts = await parsePNGTextChunks(arrayBuffer);
      out.fromContainer = parseNovelAIFromTextChunks(out.pngTexts.texts);
    } else if (mime === 'image/webp' || isWebP) {
      out.webpMeta = await parseWebPMetadata(arrayBuffer);
      out.fromContainer = parseNovelAIFromWebP(out.webpMeta);
    }
  }

  if (imageSource) {
    out.stealth = await extractStealthPNGCOMPFromImageSource(imageSource);
    out.fromStealth = parseNovelAIFromStealth(out.stealth);
  }

  out.result = out.fromContainer || out.fromStealth || null;
  return out;
}

export default {
  parsePNGTextChunks,
  parseWebPMetadata,
  extractStealthPNGCOMPFromImageSource,
  parseNovelAIFromTextChunks,
  parseNovelAIFromWebP,
  parseNovelAIFromStealth,
  parseNovelAIMetadata,
};
